import {
  PropsWithChildren,
  useRef,
  useState,
  useContext,
  useEffect,
} from 'react';
import { useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { AnimatedDialog } from '../01-core/AnimatedDialog';
import { Button } from '../02-atoms/Button';
import { Tabs } from '../03-molecules/Tabs';
import { SidepanelContext } from './SidepanelContext';

export interface SidepanelTabProps extends PropsWithChildren {
  /** The tab label */
  label: string;
  // minimal: optionale Felder, weil du sie unten reinsetzt
  id?: string;
  open?: boolean;
  'aria-haspopup'?: string;
  'aria-controls'?: string;
  'aria-expanded'?: 'true' | 'false';
}

export interface SidepanelProps extends PropsWithChildren {
  /** The unique ID of the sidepanel menu */
  id?: string;
  /** Optional tabs */
  tabs?: SidepanelTabProps[];
  /** Optional current tab (index) */
  currentTab?: number | null; // <— vorher object
  /** Optional prev tab title */
  prevTab?: string;
  /** Optional next tab title */
  nextTab?: string; // <— vorher nextTag
  /** Optional close label */
  close?: string;
  /** On tab change event handler */
  onTabChange?: (tab: number) => void; // <— statt int
}

/** Block rendering a sidepanel */
export const Sidepanel = ({
  id,
  tabs,
  currentTab = null,
  prevTab,
  nextTab,
  close,
  children,
  onTabChange,
  ...props
}: SidepanelProps) => {
  const [open, , doClose] = useContext(SidepanelContext)!;
  const [tab, setTab] = useState<number>(currentTab ?? 0); // <— als number führen
  const [stateId] = useState(id || 'sidepanel-' + uuidv4());
  const firstRender = useRef<boolean>(true);

  // State managed by parent
  if (currentTab !== null && currentTab !== tab) {
    setTab(currentTab);
  }

  const isOpen = stateId === open;

  // tabs tip-sicher behandeln (lokale Variable überschreiben ist ok)
  const mappedTabs: SidepanelTabProps[] = (tabs ?? []).map((t, i) => {
    const id = `${stateId}-tab-${i}`;
    return {
      ...t,
      id,
      open: i === tab,
      'aria-haspopup': 'true',
      'aria-controls': id,
      'aria-expanded': i === tab ? 'true' : 'false',
    };
  });

  // Close on location change
  const location = useLocation();
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    if (isOpen) {
      doClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const switchTab = (target: number | 'next' | 'prev') => {
    let next = tab;
    if (target === 'next') {
      next = mappedTabs.length
        ? tab === mappedTabs.length - 1
          ? 0
          : tab + 1
        : 0;
    } else if (target === 'prev') {
      next = mappedTabs.length
        ? tab === 0
          ? mappedTabs.length - 1
          : tab - 1
        : 0;
    } else {
      next = target;
    }
    setTab(next);
    onTabChange?.(next);
  };

  return (
    <>
      <AnimatedDialog
        id={stateId}
        open={isOpen}
        className="sidepanel"
        duration={250}
        {...props}
      >
        <Button
          className="sidepanel__close"
          title={close}
          type="primary"
          icon="cancel"
          onClick={() => {
            doClose();
          }}
          aria-haspopup="true"
          aria-controls={stateId}
          aria-expanded={isOpen ? 'true' : 'false'}
        />
        {mappedTabs.length > 1 && (
          <div className="sidepanel__tabs-navigation">
            <Button
              className="sidepanel__tabs-prev"
              title={prevTab}
              type="primary"
              size="small"
              icon="arrow_left_triangle"
              onClick={() => {
                switchTab('prev');
              }}
            />
            <Button
              className="sidepanel__tabs-next"
              title={nextTab}
              type="primary"
              size="small"
              icon="arrow_right_triangle"
              onClick={() => {
                switchTab('next');
              }}
            />
            <Tabs
              className="sidepanel__tabs"
              items={mappedTabs}
              currentTab={tab}
              onTabChange={switchTab}
              enableScroll={true}
            />
          </div>
        )}
        <div className="sidepanel__content">
          <div className="sidepanel__container">
            {children}
            {mappedTabs.map((t, i) => (
              <AnimatedDialog
                role="tabpanel"
                className="sidepanel__tab"
                key={i}
                id={t.id}
                open={t.open}
                duration={0}
              >
                {t.children}
              </AnimatedDialog>
            ))}
          </div>
        </div>
      </AnimatedDialog>
    </>
  );
};
